import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { WorkflowStepType } from '@commenda/types';
import React from 'react';
import { v4 as uuidV4 } from 'uuid';
import { WorkflowNodeHelper } from '../../../workflow/helpers/node';
import { ServiceAuditorEditorHelper } from '../../helpers';
import { TBaseNodeData } from '../../types/node';
import { NodePaletteItem } from './NodePaletteItem';

interface NodePaletteProps {
  onNodeDragStart?: (type: string, data: TBaseNodeData) => void;
}

export const NodePalette = ({ onNodeDragStart }: NodePaletteProps) => {
  const onDragStart = (
    event: React.DragEvent,
    nodeType: string,
    defaultData: Omit<TBaseNodeData, 'id'>
  ) => {
    const nodeData: TBaseNodeData = {
      ...defaultData,
      id: uuidV4(),
    };

    event.dataTransfer.setData(
      'application/reactflow',
      JSON.stringify({
        nodeType,
        nodeData,
      })
    );

    if (onNodeDragStart) onNodeDragStart(nodeType, nodeData);
  };

  return (
    <div className="p-4 overflow-y-auto flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <h4 className="txt-extra-small-medium text-text-primary">Core</h4>
        <div className="flex flex-col gap-1">
          <NodePaletteItem
            node={WorkflowNodeHelper.nodeTemplates[WorkflowStepType.START]}
            onDragStart={(e) =>
              onDragStart(
                e,
                WorkflowStepType.START,
                WorkflowNodeHelper.nodeTemplates[WorkflowStepType.START]
                  .defaultData
              )
            }
          />
          <NodePaletteItem
            node={WorkflowNodeHelper.nodeTemplates[WorkflowStepType.END]}
            onDragStart={(e) =>
              onDragStart(
                e,
                WorkflowStepType.END,
                WorkflowNodeHelper.nodeTemplates[WorkflowStepType.END]
                  .defaultData
              )
            }
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <h4 className="txt-extra-small-medium text-text-primary">Human</h4>
        <div className="flex flex-col gap-1">
          {WorkflowNodeHelper.getHumanNodeTemplates().map((node) => (
            <NodePaletteItem
              key={node.stepType}
              node={node}
              onDragStart={(e) =>
                onDragStart(e, node.stepType, node.defaultData)
              }
            />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <h4 className="txt-extra-small-medium text-text-primary">Gateway</h4>
        <div className="flex flex-col gap-1">
          {WorkflowNodeHelper.getGatewayNodeTemplates().map((node) => (
            <NodePaletteItem
              key={node.stepType}
              node={node}
              onDragStart={(e) =>
                onDragStart(e, node.stepType, node.defaultData)
              }
            />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <h4 className="txt-extra-small-medium text-text-primary">System</h4>
        <div className="flex flex-col gap-1">
          {WorkflowNodeHelper.getSystemNodeTemplates().map((node) => (
            <NodePaletteItem
              key={node.stepType}
              node={node}
              onDragStart={(e) =>
                onDragStart(e, node.stepType, node.defaultData)
              }
            />
          ))}
          {isFeatureLaunched(FeatureLaunch.SERVICE_AUDITOR) && (
            <NodePaletteItem
              node={WorkflowNodeHelper.draftAuditorTemplate}
              onDragStart={(e) =>
                onDragStart(
                  e,
                  ServiceAuditorEditorHelper.DRAFT_AUDITOR_TEMPLATE_NODE_TYPE,
                  WorkflowNodeHelper.draftAuditorTemplate.defaultData
                )
              }
            />
          )}
          <NodePaletteItem
            node={WorkflowNodeHelper.nodeTemplates[WorkflowStepType.DEMO_EMAIL]}
            onDragStart={(e) =>
              onDragStart(
                e,
                WorkflowStepType.DEMO_EMAIL,
                WorkflowNodeHelper.nodeTemplates[WorkflowStepType.DEMO_EMAIL]
                  .defaultData
              )
            }
          />
          <NodePaletteItem
            node={
              WorkflowNodeHelper.nodeTemplates[
                WorkflowStepType.DEMO_AI_FILLING_AGENT
              ]
            }
            onDragStart={(e) =>
              onDragStart(
                e,
                WorkflowStepType.DEMO_AI_FILLING_AGENT,
                WorkflowNodeHelper.nodeTemplates[
                  WorkflowStepType.DEMO_AI_FILLING_AGENT
                ].defaultData
              )
            }
          />
        </div>
      </div>
    </div>
  );
};
